import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaSpinner } from 'react-icons/fa';
import { CSVFileEntry, ZeitschritteData } from '../../../../types/common';
import { useCSVFiles } from './hooks';
import { CSVFileEntryCard } from './CSVFileEntryCard';
import FileMetadataTable from '../FileMetadataTable';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

interface CSVFileUploaderProps {
  onFilesChange?: (files: CSVFileEntry[]) => void;
  sessionId?: string;
  zeitschritte?: ZeitschritteData;
  globalDuplicateBezeichnungIds?: Set<string>;
  onUploadComplete?: () => void;
}

/**
 * Main CSV File Uploader component
 * Refactored from monolithic ~1050 line component into modular composition
 *
 * Structure:
 * - useCSVFiles hook: state management + CRUD operations
 * - CSVFileEntryCard: composition of Header + 4 Sections
 */
const CSVFileUploader: React.FC<CSVFileUploaderProps> = ({ onFilesChange, sessionId, zeitschritte, globalDuplicateBezeichnungIds, onUploadComplete }) => {
  const { t } = useTranslation('training');

  // Main state and operations from custom hook (fileType='input' for CSV input files)
  const csvFiles = useCSVFiles(sessionId, onFilesChange, 'input', zeitschritte, onUploadComplete);

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible mb-4 sm:mb-6">
      {/* Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-slate-50 to-gray-50 border-b border-border">
        <div className="flex items-center justify-center gap-1.5">
          <h2 className="text-base font-semibold text-foreground tracking-tight">
            {t('csvFileUploader.title')}
          </h2>
          <InfoTooltip text={t('tooltips.config.eingabedaten')} width="w-80" />
          {csvFiles.isLoading && (
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <FaSpinner className="animate-spin text-xs" />
              <span className="text-xs">{t('csvFileUploader.loading') || 'Loading...'}</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Messages */}
        {csvFiles.successMessage && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200/80 rounded-lg mb-4">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
            <p className="text-sm text-emerald-700">{t('csvFileUploader.messages.success')}</p>
          </div>
        )}
        {csvFiles.errorMessages.length > 0 && (
          <div className="p-4 bg-red-50 border border-red-200/80 rounded-lg mb-4">
            <ul className="space-y-1">
              {csvFiles.errorMessages.map((error, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-red-600">
                  <span className="text-red-400 mt-0.5 flex-shrink-0">&#x2022;</span>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* File entries */}
        <div role="list" className="space-y-2 flex flex-col items-center">
          {csvFiles.entries.map((entry, index) => (
            <div role="listitem" key={entry.id} className="w-full flex justify-center">
              <CSVFileEntryCard
                entry={entry}
                indexLabel={`IN${index + 1}`}
                onInputChange={(field: keyof CSVFileEntry, value: string) => csvFiles.handleInputChange(entry.id, field, value)}
                onRadioChange={(value, field) => csvFiles.handleRadioChange(entry.id, value, field)}
                onFileChange={(e) => csvFiles.handleFileChange(entry.id, e)}
                onRemove={() => csvFiles.removeEntry(entry.id)}
                onUpload={() => csvFiles.uploadEntry(entry.id)}
                onSave={() => csvFiles.saveEntry(entry.id)}
                isUploading={csvFiles.isLoading}
                isDirty={csvFiles.isEntryDirty(entry.id)}
                colorTheme="blue"
                hasDuplicateBezeichnung={globalDuplicateBezeichnungIds?.has(entry.id) ?? false}
                sessionId={sessionId}
              />
            </div>
          ))}
        </div>

        <FileMetadataTable entries={csvFiles.entries} colorTheme="blue" />
      </div>
    </div>
  );
};

export default CSVFileUploader;
